import { formatFileSize } from '@/shared/utils/file.utils';
import type { AcceptedFileTypes, FileValidator } from '@/shared/utils/upload-utils';
import { Stack, Text } from '@mantine/core';
import { Dropzone, type FileRejection } from '@mantine/dropzone';
import { IconUpload } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';

type Props = {
  /** Types de fichier autorisés ; tous les types si absent */
  accept?: AcceptedFileTypes;
  /** Taille maximale du fichier, en octets ; pas de limite si absente */
  maxSize?: number;
  /** Contrôles spécifiques du contenu, exécutés avant l'envoi */
  validate?: FileValidator;
  /**
   * Appelé avec le fichier déposé lorsqu'il respecte les contraintes ; s'il renvoie une clé
   * Tolgee (ex. échec de l'envoi), le message s'affiche avec les erreurs de validation.
   */
  onFileSelected: (file: File) => void | Promise<string | null>;
};

const getAcceptedExtensions = (accept: AcceptedFileTypes): string =>
  Object.values(accept).flat().join(', ');

const UploadFileDropzone = ({ accept, maxSize, validate, onFileSelected }: Props) => {
  const { t } = useTranslate();
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const acceptedExtensions = accept ? getAcceptedExtensions(accept) : null;
  const maxSizeLabel = maxSize ? formatFileSize(maxSize) : null;
  const hasErrors = errorMessages.length > 0;

  const getValidationMessages = async (file: File): Promise<string[]> => {
    if (!validate) {
      return [];
    }

    setIsValidating(true);

    try {
      const errors = await validate(file);
      return errors.map(({ key, params }) => t(key, params));
    } catch {
      return [t('file.upload.error')];
    } finally {
      setIsValidating(false);
    }
  };

  const handleDrop = async (files: File[]) => {
    const selectedFile = files[0];

    if (!selectedFile) {
      return;
    }

    const validationMessages = await getValidationMessages(selectedFile);
    setErrorMessages(validationMessages);

    if (validationMessages.length > 0) {
      return;
    }

    setIsProcessing(true);

    try {
      const errorKey = await onFileSelected(selectedFile);
      if (errorKey) {
        setErrorMessages([t(errorKey)]);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const getRejectionMessage = (fileRejections: FileRejection[]): string => {
    const errorCodes = fileRejections.flatMap(({ errors }) => errors.map(({ code }) => code));

    if (errorCodes.includes('file-invalid-type') && acceptedExtensions) {
      return t('file.upload.error.invalid-type', { extensions: acceptedExtensions });
    }

    if (errorCodes.includes('file-too-large') && maxSizeLabel) {
      return t('file.upload.error.too-large', { maxSize: maxSizeLabel });
    }

    return t('file.upload.error');
  };

  const handleReject = (fileRejections: FileRejection[]) => {
    setErrorMessages([getRejectionMessage(fileRejections)]);
  };

  return (
    <Stack gap="xs">
      <Dropzone
        onDrop={files => void handleDrop(files)}
        onReject={handleReject}
        accept={accept}
        maxSize={maxSize}
        multiple={false}
        maxFiles={1}
        loading={isValidating || isProcessing}
        aria-invalid={hasErrors}
        styles={{
          root: {
            borderColor: hasErrors ? 'var(--mantine-color-red-6)' : undefined,
          },
        }}
      >
        <Stack gap="xs" align="center" py="md">
          <IconUpload size={28} />
          <Text size="sm" ta="center">
            {t('file.upload.dropzone.help')}
          </Text>
          {acceptedExtensions && (
            <Text size="xs" c="dimmed" ta="center">
              {t('file.upload.dropzone.accepted-types', { extensions: acceptedExtensions })}
            </Text>
          )}
          {maxSizeLabel && (
            <Text size="xs" c="dimmed" ta="center">
              {t('file.upload.dropzone.max-size', { maxSize: maxSizeLabel })}
            </Text>
          )}
        </Stack>
      </Dropzone>
      {hasErrors && (
        <Stack gap={4} role="alert">
          {errorMessages.map(message => (
            <Text key={message} size="sm" c="red.6">
              {message}
            </Text>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default UploadFileDropzone;
